import React from "react";
import TotalMealsCountGridView from "./TotalMealsCountGridView";
import TotalMealsCardsApp from "../Functionality/TotalMealsCardsApp";
import CustomHorizontalInfiniteScroll
    from "../../../../../components/HorizontalInfiniteScroll/CustomHorizontalInfiniteScroll";
import {CustomCardGrid} from "../../../../../components/CustomCardGrid";
import {colors} from "../../../../../enums/colors";
import {IconForkKnifeCard} from "../../../../../components/buttons/IconButtons";
import CustomTooltip from "../../../../../components/CustomTooltip";
import TotalMealCardContent from "./TotalMealCardContent";
import {COURSE_TYPE_MODEL} from "../../Menu/Model/Model";
import NotInformation from "../../../../../components/NotInformation";
import TotalMealsHeader from "./TotalMealsHeader";

/**
 * @email kpozos@ibl.mx
 * @author Karen Yunuen Pozos Arias
 * @title Full Stack Developer
 *
 * @component TotalMealsCards
 * @description
 * Renders cards showing meal information per meal time.
 * - Shows total meals overview with `TotalMealsCountGridView`.
 * - Displays each meal time as a card with a list of menu items grouped by course.
 * - Highlights max/min counts per course using `TotalMealCardContent`.
 * - Uses a horizontal infinite scroll for meal time cards.
 * - If no meals are available, shows a `NotInformation` placeholder.
 *
 * @param {Object} props - Component props passed to the custom hook.
 * @returns {JSX.Element} Rendered TotalMealsCards component.
 */
const TotalMealsCards = ({
    subView,
    subViewChild,
    setSubViewChild,
    handleBtnDinning,
    handleBtnSettings, 
    handleBtnDepartmentPolicies,
    handleBtnTotalMeals,
    setBodyForExport,
    searchElement,
    setSearchElement,
    setSearch,
    setSimpleSearch,
    option,
    selectedOption,
    setSelectedOption,
    setRender,
    selectedDate,
    setSelectedDate,
    finalArray,
    prevIsDaily
}) => {

    const { t, results, setResults} = TotalMealsCardsApp({
        selectedOption
    });

    return (
        <>
            <TotalMealsHeader
                subView={subView}
                subViewChild={subViewChild}
                setSubViewChild={setSubViewChild}
                handleBtnDinning={handleBtnDinning}
                handleBtnSettings={handleBtnSettings}
                handleBtnDepartmentPolicies={handleBtnDepartmentPolicies}
                handleBtnTotalMeals={handleBtnTotalMeals}
                setBodyForExport={setBodyForExport}
                searchElement={searchElement}
                setSearchElement={setSearchElement}
                setSearch={setSearch}
                setSimpleSearch={setSimpleSearch}
                setResults={setResults}
                results={results}
                option={option}
                selectedOption={selectedOption}
                setSelectedOption={setSelectedOption}
                setRender={setRender}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                finalArray={finalArray}
                prevIsDaily={prevIsDaily}
            />

            {/* Show placeholder if no meals are recorded */}
            {results?.length === 0 ? (
                <NotInformation
                    text={t('looks_like_there_are_no_meals_recorded_yet')}
                    card={true}
                />
            ) : (
                <>
                    {/* Grid view of total meals summary */}
                    <div className={"col-12"}>
                        <TotalMealsCountGridView/>
                    </div>

                    <div className={"mt-3"}>
                        <div className={"col-12"}>
                            {/* Horizontal scrollable list of meal time cards */}
                            <CustomHorizontalInfiniteScroll
                                items={results}
                                scrollStep={450}
                                renderItem={(row) => {
                                    return (
                                        <CustomCardGrid
                                            key={row.mealTimeId}
                                            isCheckboxShow={false}
                                            rowId={row.mealTimeId}
                                            checked={false}
                                            onCheckboxChange={() => {}}
                                            disabled={false}
                                            statusColor={colors.secondary100}
                                            fontColor={colors.secondary700}
                                            borderColor={colors.secondary150}
                                            showCustomChip={false}
                                            srcImage={row.image} // Default image for the meal time
                                            isSquareImg={true}
                                            height={`menu-item-card`}
                                            isDefaultIcon={true}
                                            classNamePanelGrid={""}
                                            imgContainerClass={"d-flex justify-content-center align-items-center h-100"}
                                            headClass={"h-150px"}
                                        >
                                            <div className={"row"}>
                                                {/* Meal time title with icon */}
                                                <div className={"col-12 mb-2 h-48px"}>
                                                    <div className={"d-flex align-items-center flex-grow-1 minw-0"}>
                                                        <div className={"mr-2"}>
                                                            <IconForkKnifeCard width={24} height={24}/>
                                                        </div>
                                                        <CustomTooltip
                                                            text={row.mealTimeName}
                                                            classes={`fs-16px ibl-font-w-600 text-ellipsis-2-rows`}
                                                            showIfTruncated={true}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Scrollable list of courses for this meal time */}
                                                <div className={"h-180px overflow-y-auto"}>
                                                    <TotalMealCardContent
                                                        list={row.appetizer}
                                                        courseType={COURSE_TYPE_MODEL.STARTER}
                                                    />
                                                    <TotalMealCardContent
                                                        list={row.mainCourse}
                                                        courseType={COURSE_TYPE_MODEL.MAIN_COURSE}
                                                    />
                                                    <TotalMealCardContent
                                                        list={row.sideCourse}
                                                        courseType={COURSE_TYPE_MODEL.SIDE_COURSE}
                                                    />
                                                    <TotalMealCardContent
                                                        list={row.dessert}
                                                        courseType={COURSE_TYPE_MODEL.DESSERT}
                                                    />
                                                </div>
                                            </div>
                                        </CustomCardGrid>
                                    );
                                }}
                            />
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default TotalMealsCards;
